import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Megaphone, CheckCircle2, Send, ChevronRight, MapPin, ExternalLink } from "lucide-react";
import TiltCard from "../components/TiltCard";
import APMap from "../components/APMap";
import Icon3D from "../components/Icon3D";
import Carousel3D from "../components/Carousel3D";
import Countdown from "../components/Countdown";
import { useT } from "../i18n";
import { useNavigate } from "react-router-dom";
import { SectionTitle } from "../components/PageBanner";
import { stats, colleges, galleryImages, announcements, quickServices, objectives } from "../data/mock";

function useCountUp(target, active) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const dur = 1400, t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, target]);
  return n;
}

function Stat({ value, label, icon }) {
  const { t } = useT();
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setActive(true), { threshold: 0.4 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const n = useCountUp(value, active);
  return (
    <div ref={ref} style={{ textAlign: "center" }}>
      <Icon3D name={icon} size={72} float />
      <div className="kpi-num" style={{ marginTop: 10 }} data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{n.toLocaleString()}</div>
      <div style={{ fontSize: 13, color: "#44403C", fontWeight: 600, marginTop: 6 }}>{t(`stat.${label}`)}</div>
    </div>
  );
}

function Reveal({ children, delay = 0, style }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => el.classList.add("in"), delay); io.disconnect(); }
    }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return <div ref={ref} className="reveal" style={style}>{children}</div>;
}

function Hero() {
  const { t } = useT();
  return (
    <section data-testid="hero-section" className="hero-section">
      <div className="hero-img" style={{ backgroundImage: "url(/img/hero-banner.jpg)" }}>
        <div className="hero-overlay" />
        <div className="hero-inner">
          <div className="hero-copy">
            <span className="badge badge-live" data-testid="hero-live-badge">{t("hero.badge")}</span>
            <h1 className="font-serif hero-h1">
              {t("hero.h1a")} <span className="text-grad">{t("hero.h1b")}</span> {t("hero.h1c")}
            </h1>
            <p className="hero-lead">{t("hero.p")}</p>
            <div className="hero-actions">
              <Link to="/admissions" style={{ textDecoration: "none" }}><button className="btn-primary-3d" data-testid="hero-register-btn">{t("btn.register")} <ArrowRight size={16} style={{ marginLeft: 8, verticalAlign: "middle" }} /></button></Link>
              <Link to="/about" style={{ textDecoration: "none" }}><button className="btn-yellow-3d" data-testid="hero-about-btn">{t("btn.about")}</button></Link>
            </div>
            <div className="hero-countdown"><Countdown size="compact" /></div>
          </div>
        </div>
      </div>
      <div className="tricolor-line" />
    </section>
  );
}

function Announcements() {
  const { t } = useT();
  return (
    <div data-testid="announcement-bar" style={{ background: "#FFF7ED", borderBottom: "1px solid rgba(249,115,22,0.2)", display: "flex", alignItems: "center" }}>
      <div className="announce-label"><Megaphone size={18} /> {t("announcements")}</div>
      <div className="ticker" style={{ flex: 1, marginLeft: 36, padding: "12px 0" }}>
        <div className="ticker-track">
          {[...announcements, ...announcements].map((a, i) => (
            <Link key={i} to="/circulars" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 10, color: "#1C1917", fontSize: 14, fontWeight: 500 }}>
              {a.urgent && <span className="badge badge-red" style={{ padding: "2px 8px", fontSize: 9.5 }}>New</span>}
              <span style={{ color: "#EA580C", fontWeight: 700 }}>{a.date}</span> {a.text}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function AboutSection() {
  const { t } = useT();
  return (
    <section style={{ padding: "60px 24px 30px", position: "relative" }} className="soft-bg">
      <div className="blob" style={{ width: 380, height: 380, left: -120, top: 40, background: "rgba(250,204,21,0.25)" }} />
      <div style={{ maxWidth: 1440, margin: "0 auto", display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 36, alignItems: "center", position: "relative" }} className="two-col">
        <Reveal>
          <SectionTitle>{t("sec.about")}</SectionTitle>
          <h2 style={{ fontSize: "1.7rem", fontWeight: 600, color: "#1C1917", lineHeight: 1.35, marginTop: 4 }}>
            APMERCL serves Andhra Pradesh through <span style={{ color: "#EA580C", fontWeight: 800 }}>7 Government Medical Colleges</span>, 900 MBBS seats and 734 faculty members.
          </h2>
          <p style={{ color: "#44403C", fontSize: 15.5, lineHeight: 1.9, marginTop: 16, textAlign: "justify" }}>
            The Andhra Pradesh Medical Education and Research Corporation Limited (APMERCL) was established by the Government of Andhra Pradesh with the objective of strengthening medical education and healthcare infrastructure in the State through the planning, development, establishment, management, and maintenance of Government Medical Colleges, Teaching Hospitals, Nursing Colleges, Super Specialty Hospitals, and other healthcare institutions. The Corporation is also entrusted with the implementation of Government policies, execution of infrastructure projects, efficient utilization of financial resources, and ensuring effective administration of its institutions.
          </p>
          <ul style={{ marginTop: 16, paddingLeft: 0, listStyle: "none", display: "grid", gap: 9 }}>
            {objectives.slice(0, 4).map((o, i) => (
              <li key={i} style={{ display: "flex", gap: 10, fontSize: 14.5, color: "#1C1917", lineHeight: 1.5 }}>
                <CheckCircle2 size={18} color="#F97316" style={{ flexShrink: 0, marginTop: 2 }} /> {o}
              </li>
            ))}
          </ul>
          <Link to="/about" data-testid="about-read-more" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 22, color: "#EA580C", fontWeight: 800, textDecoration: "none", fontSize: 16 }}>
            {t("btn.readMore")} <ChevronRight size={16} /><ChevronRight size={16} style={{ marginLeft: -12 }} />
          </Link>
        </Reveal>
        <Reveal delay={120}>
          <TiltCard intensity={4}>
            <div style={{ borderRadius: 20, overflow: "hidden", position: "relative", aspectRatio: "4/3" }}>
              <img data-testid="about-cm-photo" src="/img/events/7.jpg" alt="Hon'ble Chief Minister Shri Nara Chandrababu Naidu inaugurating the Centenary Building at Andhra Medical College" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 20%" }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 50%, rgba(124,45,18,0.88))" }} />
              <div style={{ position: "absolute", left: 20, right: 20, bottom: 16, color: "#fff" }}>
                <span className="badge badge-yellow">Hon'ble Chief Minister</span>
                <div style={{ fontSize: 17, fontWeight: 700, marginTop: 8, lineHeight: 1.3 }}>Shri Nara Chandrababu Naidu inaugurates the Centenary Building at Andhra Medical College, Visakhapatnam</div>
                <div style={{ fontSize: 12.5, opacity: 0.9, marginTop: 2 }}>26 April 2025 · KGH to be developed as a Centre of Excellence on the lines of AIIMS</div>
              </div>
            </div>
          </TiltCard>
        </Reveal>
      </div>
    </section>
  );
}

function MapSection() {
  const { t } = useT();
  const [hl, setHl] = useState(null);
  const nav = useNavigate();
  const districtOf = { eluru: "Eluru", machilipatnam: "Krishna", nandyal: "Nandyal", paderu: "Alluri Sitharama Raju", piduguralla: "Palnadu", rajamahendravaram: "East Godavari", vizianagaram: "Vizianagaram" };
  return (
    <section style={{ padding: "40px 24px 50px" }}>
      <div style={{ maxWidth: 1440, margin: "0 auto" }}>
        <Reveal><SectionTitle>{t("sec.map")}</SectionTitle></Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 30, alignItems: "stretch" }} className="two-col">
          <Reveal>
            <div style={{ position: "relative", padding: "34px 22px 40px", border: "2px dashed #FB923C", borderRadius: 16, background: "#fff", height: "100%" }}>
              <div className="map-tab" style={{ left: 0, top: -18 }}>26 Districts</div>
              <div className="map-tab" style={{ right: 0, top: -18, background: "linear-gradient(180deg,#FDE047,#FACC15)", color: "#713F12" }}>7 Medical Colleges</div>
              <div className="map-tab" style={{ left: 0, bottom: -18, background: "linear-gradient(180deg,#FDE047,#FACC15)", color: "#713F12" }}>900 MBBS Seats</div>
              <div className="map-tab" style={{ right: 0, bottom: -18 }}>734 Faculty</div>
              <APMap highlight={hl} />
              <div style={{ position: "absolute", left: 24, bottom: 18, display: "flex", gap: 14, fontSize: 11.5, fontWeight: 600, color: "#44403C" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: "#DC2626", display: "inline-block" }} /> {t("map.legend")}</span>
                <span>{t("map.hint")}</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="premium-card" data-testid="college-list-panel" style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <div style={{ background: "linear-gradient(90deg,#F97316,#FACC15)", padding: "14px 20px", color: "#fff", fontWeight: 800, letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 8 }}>
                <Icon3D name="hospital" size={34} /> {t("map.list")}
              </div>
              <div style={{ display: "grid", gap: 2, padding: 10, flex: 1 }}>
                {colleges.map((c, i) => (
                  <div key={c.slug} data-testid={`college-list-${c.slug}`} onMouseEnter={() => setHl(districtOf[c.slug])} onMouseLeave={() => setHl(null)} onClick={() => nav(`/colleges/${c.slug}`)}
                    style={{ display: "grid", gridTemplateColumns: "56px 1fr auto", gap: 12, alignItems: "center", padding: "10px 12px", borderRadius: 12, cursor: "pointer", transition: "background 200ms, transform 200ms", background: hl === districtOf[c.slug] ? "#FFF7ED" : "transparent", transform: hl === districtOf[c.slug] ? "translateX(4px)" : "none", borderLeft: `4px solid ${["#DC2626", "#65A30D", "#06B6D4", "#22C55E", "#7C3AED", "#2563EB", "#3B82F6"][i]}` }}>
                    <div style={{ width: 56, height: 44, borderRadius: 8, overflow: "hidden" }}><img src={c.img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" /></div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13.5, color: "#1C1917" }}>{c.name}</div>
                      <div style={{ fontSize: 11.5, color: "#78716C", display: "flex", gap: 6, alignItems: "center" }}><MapPin size={11} color="#F97316" /> {c.district} · {c.seats} seats</div>
                    </div>
                    <a href={c.website} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} aria-label="Official website" style={{ color: "#EA580C" }}><ExternalLink size={15} /></a>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Showcase() {
  const { t } = useT();
  const nav = useNavigate();
  const items = colleges.map(c => ({ url: c.img, title: c.name, category: c.district, slug: c.slug }));
  return (
    <section style={{ padding: "30px 24px 50px" }} className="soft-bg">
      <div style={{ maxWidth: 1440, margin: "0 auto" }}>
        <Reveal><SectionTitle>{t("sec.campus")}</SectionTitle></Reveal>
        <Reveal><Carousel3D items={items} height={420} onSelect={(it) => nav(`/colleges/${it.slug}`)} /></Reveal>
      </div>
    </section>
  );
}

function Services() {
  const { t } = useT();
  return (
    <section style={{ padding: "50px 24px" }}>
      <div style={{ maxWidth: 1440, margin: "0 auto" }}>
        <Reveal><SectionTitle>{t("sec.services")}</SectionTitle></Reveal>
        <div className="grid-eq grid-6" style={{ display: "grid", gap: 18 }}>
          {quickServices.map((s, i) => {
            return (
              <Reveal key={s.path} delay={i * 70}>
                <Link to={s.path} data-testid={`service-${s.path === "/" ? "home" : s.path.slice(1)}`} style={{ textDecoration: "none", display: "block" }}>
                  <TiltCard intensity={6}>
                    <div style={{ padding: 22 }}>
                      <Icon3D name={s.icon} size={74} />
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#1C1917", marginTop: 16 }}>{s.title}</div>
                      <div style={{ fontSize: 13, color: "#78716C", marginTop: 6, lineHeight: 1.55 }}>{s.desc}</div>
                      <div style={{ marginTop: 14, color: "#EA580C", fontWeight: 700, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>{t("btn.open")} <ArrowRight size={13} /></div>
                    </div>
                  </TiltCard>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function NewsAndReach() {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", phone: "", college: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    const mobile = form.phone.replace(/\s/g, "");
    if (!/^[6-9]\d{9}$/.test(mobile)) { window.alert("Enter a valid 10-digit mobile number."); return; }
    const collegeName = colleges.find(c => c.slug === form.college)?.name || form.college;
    const body = `Name: ${form.name}\nCollege: ${collegeName}\nMobile: ${mobile}\n\n${form.message}`;
    window.location.href = `mailto:md.apmerc@gmail.com?subject=${encodeURIComponent("APMERCL website enquiry")}&body=${encodeURIComponent(body)}`;
    setSent(true);
    setTimeout(() => { setSent(false); setForm({ name: "", phone: "", college: "", message: "" }); }, 3000);
  };
  return (
    <section style={{ padding: "40px 24px 60px" }} className="soft-bg">
      <div style={{ maxWidth: 1440, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 36 }} className="two-col">
        <Reveal>
          <SectionTitle>{t("sec.new")}</SectionTitle>
          <div className="note-card" data-testid="whats-new-card" style={{ padding: 22, minHeight: 420 }}>
            <Megaphone size={260} style={{ position: "absolute", right: -40, top: 40, color: "rgba(249,115,22,0.09)", transform: "rotate(-15deg)" }} />
            <div style={{ display: "grid", gap: 14, position: "relative" }}>
              {announcements.map(a => (
                <div key={a.id} style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 14, alignItems: "start" }}>
                  <span className="date-chip">{a.date}</span>
                  <div style={{ color: "#C2410C", fontWeight: 600, fontSize: 14, lineHeight: 1.55, paddingTop: 6 }}>
                    {a.urgent && <span className="badge badge-red" style={{ marginRight: 8, padding: "2px 8px", fontSize: 9.5 }}>New</span>}{a.text}
                  </div>
                </div>
              ))}
            </div>
            <Link to="/circulars" data-testid="whats-new-view-all" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 20, color: "#EA580C", fontWeight: 800, textDecoration: "none", position: "relative" }}>View all circulars <ChevronRight size={16} /></Link>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <SectionTitle>{t("sec.reach")}</SectionTitle>
          <div className="panel-orange" data-testid="reach-us-panel" style={{ padding: 26, position: "relative", overflow: "hidden", minHeight: 420 }}>
            <img src={galleryImages[1].url} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.18, mixBlendMode: "luminosity" }} />
            <div style={{ position: "relative" }}>
              <p style={{ fontSize: 15.5, fontWeight: 600, marginBottom: 18 }}>{t("reach.p")}</p>
              {sent ? (
                <div style={{ textAlign: "center", padding: "50px 20px" }}>
                  <CheckCircle2 size={56} style={{ color: "#FEF08A" }} />
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 12 }}>Thank you! We'll respond within 2 working days.</div>
                </div>
              ) : (
                <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>{t("reach.name")}
                    <input data-testid="reach-name-input" required className="input-3d" style={{ marginTop: 6, border: "none" }} placeholder="Enter Full Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                  </label>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>{t("reach.college")}
                    <select data-testid="reach-college-select" required className="input-3d" style={{ marginTop: 6, border: "none" }} value={form.college} onChange={e => setForm({ ...form, college: e.target.value })}>
                      <option value="">Select College</option>
                      {colleges.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                  </label>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>{t("reach.phone")}
                    <input data-testid="reach-phone-input" required className="input-3d" style={{ marginTop: 6, border: "none" }} placeholder="Enter Phone Number" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                  </label>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>{t("reach.msg")}
                    <textarea data-testid="reach-message-input" required rows={4} className="input-3d" style={{ marginTop: 6, border: "none", resize: "vertical" }} placeholder="Write your message/query here..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
                  </label>
                  <button type="submit" className="btn-red" data-testid="reach-submit-btn" style={{ marginTop: 6, justifySelf: "center", width: "min(300px, 100%)" }}>{t("btn.submit")} <Send size={14} style={{ marginLeft: 8, verticalAlign: "middle" }} /></button>
                </form>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useT();
  return (
    <div className="page-enter">
      <Hero />
      <Announcements />

      {/* STATS */}
      <section style={{ padding: "50px 24px 30px", position: "relative" }} className="grid-bg">
        <div className="stats-grid grid-eq grid-4" style={{ maxWidth: 1440, margin: "0 auto", display: "grid", gap: 18 }}>
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 60}>
              <TiltCard intensity={4}>
                <div style={{ padding: "24px 20px", borderTop: `4px solid ${["#F97316", "#FACC15", "#DC2626", "#FB923C", "#EAB308"][i]}`, borderRadius: 20 }}>
                  <Stat value={s.value} label={s.label} icon={s.icon} />
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </section>

      <AboutSection />
      <MapSection />
      <Services />
      <Showcase />

      {/* Colleges */}
      <section className="home-colleges" style={{ padding: "40px 24px 60px" }}>
        <div style={{ maxWidth: 1440, margin: "0 auto" }}>
          <SectionTitle>{t("sec.colleges")}</SectionTitle>
          <div className="home-colleges-grid">
            {colleges.slice(0, 4).map((c) => (
              <Link key={c.slug} to={`/colleges/${c.slug}`} data-testid={`college-card-${c.slug}`} className="home-college-link">
                <TiltCard intensity={5}>
                  <div className="college-card-photo">
                    <img src={c.img} alt={c.name} />
                    <span className="badge badge-yellow">Est. {c.established}</span>
                  </div>
                  <div className="college-card-body">
                    <div className="college-card-district">{c.district.toUpperCase()}</div>
                    <div className="font-serif college-card-name">{c.name}</div>
                    <div className="college-card-meta">
                      <div className="college-card-stat">
                        <span className="college-card-stat-value">{c.seats}</span>
                        <span className="college-card-stat-label">MBBS seats</span>
                      </div>
                      <div className="college-card-stat">
                        <span className="college-card-stat-value">{c.students}</span>
                        <span className="college-card-stat-label">Students</span>
                      </div>
                    </div>
                  </div>
                </TiltCard>
              </Link>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 32 }}>
            <Link to="/colleges" style={{ textDecoration: "none" }}><button className="btn-outline-3d" data-testid="view-all-colleges-btn">{t("btn.viewAll")} <ArrowRight size={14} style={{ marginLeft: 6, verticalAlign: "middle" }} /></button></Link>
          </div>
        </div>
      </section>

      <NewsAndReach />

      {/* CTA */}
      <section style={{ padding: "20px 24px 70px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <TiltCard intensity={3}>
            <div className="panel-dark grain" style={{ padding: "50px 40px", borderRadius: 20, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", right: -20, top: -20, opacity: 0.16, pointerEvents: "none" }}><img src="/img/apmercl-logo.png" alt="" style={{ width: "min(240px, 40vw)", height: "auto" }} /></div>
              <div style={{ position: "relative", zIndex: 2, maxWidth: 700 }}>
                <span className="badge badge-yellow">Admissions Open</span>
                <h2 className="font-serif" style={{ fontSize: "2.3rem", marginTop: 14, lineHeight: 1.15, fontWeight: 800 }}>{t("cta.h")}</h2>
                <p style={{ marginTop: 12, fontSize: 15, color: "rgba(255,255,255,0.9)", maxWidth: 520 }}>{t("cta.p")}</p>
                <Link to="/admissions" style={{ textDecoration: "none" }}><button className="btn-yellow-3d" data-testid="cta-register-btn" style={{ marginTop: 22 }}>{t("cta.btn")} <ArrowRight size={16} style={{ marginLeft: 8, verticalAlign: "middle" }} /></button></Link>
              </div>
            </div>
          </TiltCard>
        </div>
      </section>

      <style>{`
        @media (max-width: 1000px) {
          .events-grid { grid-template-columns: 1fr 1fr !important; grid-template-rows: auto !important; }
          .events-grid > * { grid-row: auto !important; }
          .two-col { grid-template-columns: 1fr !important; }
          .feature-card { grid-template-columns: 1fr !important; }
          .hide-mobile { display: none !important; }
        }
        @media (max-width: 640px) {
          .stats-grid { grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
          .stats-grid .kpi-num { font-size: 1.7rem !important; }
          .panel-dark { padding: 28px 18px !important; }
          .panel-dark h2 { font-size: 1.45rem !important; }
        }
      `}</style>
    </div>
  );
}
